import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtEUR } from "@/lib/constants";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { Loader2, PhoneCall, UserCheck, Star, FileText, Trophy } from "lucide-react";

const FUNNEL_COLORS = ["#3b82f6", "#06b6d4", "#6366f1", "#a855f7", "#10b981"];

export default function Analytics() {
  const { data, isLoading } = useQuery({
    queryKey: ["analytics"],
    queryFn: () => api.get("/analytics").then((r) => r.data),
  });

  if (isLoading || !data) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const cards = [
    { label: "Kontaktversuche", value: data.call_attempts, icon: PhoneCall, tint: "text-blue-400" },
    { label: "Erreichte Entscheider", value: data.reached_decision_makers, icon: UserCheck, tint: "text-cyan-400" },
    { label: "Interessierte Leads", value: data.interested_leads, icon: Star, tint: "text-indigo-400" },
    { label: "Angebote", value: data.offers, icon: FileText, tint: "text-purple-400" },
    { label: "Kunden", value: data.customers, icon: Trophy, tint: "text-emerald-400" },
  ];

  const maxFunnel = Math.max(...data.funnel.map((f) => f.count), 1);
  const topLead = data.funnel[0]?.count || 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 fade-in">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Sales Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">Nur echte CRM-Daten — keine erfundenen Zahlen.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={`analytics-${c.label}`} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{c.label}</span>
              <c.icon className={`h-4 w-4 ${c.tint}`} />
            </div>
            <div className="mt-2 text-2xl font-bold">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Funnel */}
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="text-lg font-semibold mb-4">Conversion-Funnel</h2>
          <div className="space-y-3">
            {data.funnel.map((f, i) => {
              const pct = topLead ? Math.round((f.count / topLead) * 100) : 0;
              return (
                <div key={f.stage} data-testid={`funnel-${f.stage}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium">{f.stage}</span>
                    <span className="text-muted-foreground">{f.count} · {pct}%</span>
                  </div>
                  <div className="h-7 rounded-lg bg-accent overflow-hidden">
                    <div className="h-full rounded-lg transition-all flex items-center justify-end pr-2"
                      style={{ width: `${Math.max((f.count / maxFunnel) * 100, 4)}%`, backgroundColor: FUNNEL_COLORS[i] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Lost reasons */}
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="text-lg font-semibold mb-4">Häufigste Ablehnungsgründe</h2>
          {data.lost_reasons.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Noch keine verlorenen Leads.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.lost_reasons} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" stroke="#64748b" fontSize={12} allowDecimals={false} />
                <YAxis type="category" dataKey="reason" stroke="#94a3b8" fontSize={12} width={130} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, color: "#f8fafc" }} cursor={{ fill: "rgba(148,163,184,0.08)" }} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {data.lost_reasons.map((_, i) => <Cell key={i} fill="#f43f5e" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
